import { lazy } from 'react'
import { Navigate, createBrowserRouter } from 'react-router-dom'
import Coupons from '../pages/coupons/index.jsx'
import CandidateReport from '../pages/public-share/CandidateReport.jsx'
import CandidatePrivateRoute from './CandidatePrivateRoute'
import CandidateRestrictedRoute from './CandidateRestrictedRoute'
import PrivateRoute from './PrivateRoute'
import RestrictedRoute from './RestrictedRoute'

// **App Import
import App from '../App'

// **Context Providers that need router hooks
import MediapipeProvider from '../context/media-pipe'
import MeetingProvider from '../context/meeting'
import { SocketProvider } from '../context/socket'

// **SCSS Imports
import './index.scss'

// **Passwordless Route Imports
const PasswordlessRoute = lazy(() => import('./PasswordlessRoute.jsx'))

// **Auth Imports
const Login = lazy(() => import('../pages/auth/login'))
const Signup = lazy(() => import('../pages/auth/signup'))
const VerifyUser = lazy(() => import('../pages/auth/verify-user'))
const VerifyInvitedUser = lazy(() => import('../pages/auth/verify-invite-user'))
const ForgotPassword = lazy(() => import('../pages/auth/forgot-password'))
const ForgotPasswordVerify = lazy(
  () => import('../pages/auth/forgot-password/verify/index')
)

// **Home Import
const HomeContainer = lazy(() => import('../pages/home/HomeContainer'))

// **Subscription Imports
const Subscription = lazy(() => import('../pages/subscription'))

// **Subscription Imports
const NoWorkspace = lazy(() => import('../pages/no-workspace'))

// **Dev user route imports
const TenantManager = lazy(() => import('../pages/tenant-manager/index'))
const Attempts = lazy(() => import('../pages/attempts/index.jsx'))
const BlacklistedDomain = lazy(
  () => import('../pages/blacklisted-domains/index')
)
const ReleaseNotes = lazy(() => import('../pages/release-notes'))
const CreateUpdateReleaseNotes = lazy(
  () => import('../pages/release-notes/create')
)
const TenantInvoices = lazy(() => import('../pages/invoices/index'))
const AiTokenHistory = lazy(() => import('../pages/ai-token-history/index'))

// **Contest Import
const RegistrationForm = lazy(() => import('../pages/registration-form'))
const CreateRegistrationForm = lazy(
  () => import('../pages/registration-form/create/index')
)
const RegistrationFormContainer = lazy(
  () => import('../pages/registration-form/RegistrationFormContainer')
)
const ContestContainer = lazy(() => import('../pages/contest'))
const ContestList = lazy(() => import('../pages/contest/contest-list'))
const ContestCreate = lazy(() => import('../pages/contest/create/create'))
const EditContest = lazy(() => import('../pages/contest/edit'))
const StageDetails = lazy(
  () => import('../pages/contest/edit/stage/stage-details')
)
const StagePreview = lazy(
  () => import('../pages/contest/edit/stage/stage-preview')
)
const EvaluateAttempt = lazy(
  () => import('../pages/contest/edit/stage/EvaluateAttempt')
)

const EvaluateMCQAttempt = lazy(
  () => import('../pages/contest/edit/stage/EvaluateAttempt/EvaluateMCQAttempt')
)

const EvaluateAIScreening = lazy(
  () => import('../pages/contest/edit/stage/EvaluateAttempt/ai-screening/index')
)

const SuspiciousActivity = lazy(
  () => import('../pages/contest/edit/stage/suspicious-activity')
)
const CandidateSummary = lazy(
  () => import('../pages/contest/edit/components/CandidateSummary')
)

// **Challenge Import
const ChallengeContainer = lazy(() => import('../pages/challenge'))
const ChallengeList = lazy(() => import('../pages/challenge/challenge-list'))
const CreateChallengeWrapper = lazy(
  () => import('../pages/challenge/create/CreateChallengeWrapper')
)
const EditChallenge = lazy(() => import('../pages/challenge/edit'))
const ChallengeEditor = lazy(() => import('../pages/challenge/editor'))
const SQLChallengeEditor = lazy(
  () => import('../pages/challenge/edit/sql/editor')
)

// ** Question bank Import
const Questions = lazy(() => import('../pages/questions'))

// **Settings Imports
const SettingsContainer = lazy(() => import('../pages/settings'))
const ProfileSettings = lazy(() => import('../pages/settings/profile'))
const UsersSettings = lazy(() => import('../pages/settings/users'))
const ThemeConfigSettings = lazy(() => import('../pages/settings/config'))
const WorkSpaceSettings = lazy(() => import('../pages/settings/workspace'))
const Integrations = lazy(() => import('../pages/settings/integrations'))
const CareersConfig = lazy(() => import('../pages/careers-config'))

// **Candidate Details
const CandidateDetailsList = lazy(() => import('../pages/candidate-details'))
const CandidateDetailsEdit = lazy(
  () => import('../pages/candidate-details/edit')
)

// Candidate Imports
const CandidateContainer = lazy(() => import('../pages/candidate'))
const ContestHome = lazy(() => import('../pages/candidate/contest'))
const CandidateRegistration = lazy(
  () => import('../pages/candidate/registration')
)
const CandidateRegistrationDone = lazy(
  () => import('../pages/candidate/registration/complete')
)
const ContestStage = lazy(() => import('../pages/candidate/contest-stage'))
const DemoChallenge = lazy(
  () =>
    import('../pages/candidate/contest-stage/programming-type/demo-challenge')
)
const CandidateHome = lazy(() => import('../pages/candidate/home'))
const AuxiliaryDevice = lazy(
  () => import('../pages/candidate/auxiliary-device')
)
const CandidateResult = lazy(
  () => import('../pages/candidate/home/practice/CandidateResult')
)

// Interview Stage
const InterviewStage = lazy(() => import('../pages/interview-stage'))

// Databases Imports
const DatabaseContainer = lazy(
  () => import('../pages/databases/database-container')
)
const Databases = lazy(() => import('../pages/databases'))
const DatabaseDetailsContainer = lazy(
  () => import('../pages/databases/database-details')
)

// Course Imports
const CourseContainer = lazy(() => import('../pages/courses'))

// Hiring Course Imports
const HiringCourses = lazy(() => import('../pages/courses/hiring'))
const HiringDetails = lazy(() => import('../pages/courses/hiring/edit/index'))
const TopicDetails = lazy(
  () => import('../pages/courses/hiring/topic/edit/index')
)
const PageDetails = lazy(
  () => import('../pages/courses/hiring/topic/page/edit/index')
)

// Learner Course Imports
const LearnerCourses = lazy(() => import('../pages/courses/learner'))
const LearnerCourseDetails = lazy(
  () => import('../pages/courses/learner/details')
)
const LearnerCourseTopicPageDetails = lazy(
  () => import('../pages/courses/learner/topic/page')
)

// Evaluation Template
const EvaluationTemplateContainer = lazy(() => import('../pages/template'))
const EvaluationTemplateList = lazy(() => import('../pages/template/list'))
const CreateEvaluationTemplate = lazy(() => import('../pages/template/create'))

// Email template
const EmailTemplateContainer = lazy(
  () => import('../pages/email-template/EmailTemplateContainer')
)
const EmailTemplateList = lazy(() => import('../pages/email-template/list'))
const EditEmailTemplate = lazy(
  () => import('../pages/email-template/edit/index')
)

// Resume Pool
const ResumePool = lazy(() => import('../pages/resume-pool/index.jsx'))

// AI Interview related components
const AIScreeningStage = lazy(() => import('../pages/ai-screening-stage'))

// Video Response State
const VideoResponseStageType = lazy(
  () => import('../pages/candidate/contest-stage/video-response-stage')
)

const VideoResponseEvaluation = lazy(
  () =>
    import('../pages/contest/edit/stage/EvaluateAttempt/video-response-evaluate/index')
)

// Tenant approvals
const UserApprovals = lazy(() => import('../pages/settings/user-approvals'))

// Public Share Container
const PublicShareContainer = lazy(
  () => import('../pages/public-share/index.jsx')
)

// Linkedin Callback
const LinkedinCallback = lazy(
  () => import('../pages/auth/linkedin-callback/index.jsx')
)

const router = createBrowserRouter([
  {
    element: (
      <SocketProvider>
        <MeetingProvider>
          <MediapipeProvider>
            <App />
          </MediapipeProvider>
        </MeetingProvider>
      </SocketProvider>
    ),
    children: [
      // linkedin callback url
      {
        path: 'auth/linkedin/callback',
        element: <LinkedinCallback />
      },
      {
        path: '/login',
        element: <RestrictedRoute />,
        children: [{ index: true, element: <Login /> }]
      },
      {
        path: '/signup',
        element: <RestrictedRoute />,
        children: [{ index: true, element: <Signup /> }]
      },
      {
        path: '/verify-user/:hash',
        element: <RestrictedRoute />,
        children: [{ index: true, element: <VerifyUser /> }]
      },
      {
        path: '/verify-invited-user/:hash',
        element: <VerifyInvitedUser />
      },
      {
        path: '/forgot-password',
        element: <RestrictedRoute />,
        children: [{ index: true, element: <ForgotPassword /> }]
      },
      {
        path: '/forgot-password/verify',
        element: <RestrictedRoute />,
        children: [{ index: true, element: <ForgotPasswordVerify /> }]
      },
      {
        path: '/',
        element: <PrivateRoute />,
        children: [
          { index: true, element: <HomeContainer /> },
          { path: 'subscription', element: <Subscription /> },
          { path: 'no-workspace', element: <NoWorkspace /> },
          { path: 'question-bank-management', element: <Questions /> },
          { path: 'tenant-manager', element: <TenantManager /> },
          { path: 'attempts', element: <Attempts /> },
          { path: 'tenant-invoices', element: <TenantInvoices /> },
          { path: 'coupons', element: <Coupons /> },
          { path: 'blacklisted-domain', element: <BlacklistedDomain /> },
          { path: 'release-notes', element: <ReleaseNotes /> },
          {
            path: 'release-notes/create',
            element: <CreateUpdateReleaseNotes />
          },
          {
            path: 'release-notes/view/:id',
            element: <CreateUpdateReleaseNotes />
          },
          {
            path: 'ai-token-usage',
            element: <AiTokenHistory />
          },
          {
            path: 'application-form',
            element: <RegistrationFormContainer />,
            children: [
              { index: true, element: <RegistrationForm /> },
              { path: 'create', element: <CreateRegistrationForm /> },
              { path: 'edit/:formId', element: <CreateRegistrationForm /> }
            ]
          },
          {
            path: 'databases',
            element: <DatabaseContainer />,
            children: [
              { index: true, element: <Databases /> },
              { path: ':id/edit', element: <DatabaseDetailsContainer /> },
              { path: '*', element: <Navigate to={''} replace /> }
            ]
          },
          {
            path: 'evaluation-template',
            element: <EvaluationTemplateContainer />,
            children: [
              { index: true, element: <EvaluationTemplateList /> },
              { path: 'create', element: <CreateEvaluationTemplate /> },
              { path: 'edit/:id', element: <CreateEvaluationTemplate /> },
              { path: '*', element: <Navigate replace to={''} /> }
            ]
          },
          {
            path: 'email-template',
            element: <EmailTemplateContainer />,
            children: [
              { index: true, element: <EmailTemplateList /> },
              { path: 'edit/:templateId', element: <EditEmailTemplate /> },
              { path: '*', element: <Navigate replace to={''} /> }
            ]
          },
          { path: 'resume-pool', element: <ResumePool /> },
          { path: 'candidate-details', element: <CandidateDetailsList /> },
          {
            path: 'candidate-details/edit/:candidateId',
            element: <CandidateDetailsEdit />
          },
          {
            path: 'courses',
            element: <CourseContainer />,
            children: [
              { index: true, element: <HiringCourses /> },
              { path: ':courseId/edit', element: <HiringDetails /> },
              {
                path: ':courseId/topics/:topicId/edit',
                element: <TopicDetails />
              },
              {
                path: ':courseId/topics/:topicIds/pages/:pageId/edit',
                element: <PageDetails />
              },
              { path: '*', element: <Navigate to={''} replace /> }
            ]
          },
          {
            path: 'learning/courses',
            element: <CourseContainer />,
            children: [
              { index: true, element: <LearnerCourses /> },
              { path: ':id', element: <LearnerCourseDetails /> },
              {
                path: ':id/topics/:topicId/pages/:pageId',
                element: <LearnerCourseTopicPageDetails />
              },
              { path: '*', element: <Navigate to={''} replace /> }
            ]
          },
          {
            path: 'contests',
            element: <ContestContainer key="contest" />,
            children: [
              { index: true, element: <ContestList /> },
              { path: 'create', element: <ContestCreate /> },
              {
                path: ':contestId/ppt-stage/:stageId',
                element: <InterviewStage ppt={true} />
              },
              {
                path: ':contestId/interview-stage/:stageId/candidate/:candidateId',
                element: <InterviewStage />
              },
              { path: ':contestId/edit', element: <EditContest /> },
              {
                path: ':contestId/edit/stage/:stageId',
                element: <StageDetails />
              },
              {
                path: ':contestId/edit/stage/:stageId/mcq-evaluate/:attemptId',
                element: <EvaluateMCQAttempt />
              },
              {
                path: ':contestId/edit/stage/:stageId/psychometric-evaluate/:attemptId',
                element: <EvaluateMCQAttempt />
              },
              {
                path: ':contestId/edit/stage/:stageId/video-response-evaluate/:attemptId',
                element: <VideoResponseEvaluation />
              },
              {
                path: ':contestId/edit/stage/:stageId/ai-screening-evaluate/:attemptId',
                element: <EvaluateAIScreening />
              },
              {
                path: ':contestId/edit/stage/:stageId/evaluate/:attemptId',
                element: <EvaluateAttempt />
              },
              {
                path: ':contestId/edit/stage/:stageId/suspicious-activity/:attemptId',
                element: <SuspiciousActivity />
              },
              {
                path: ':contestId/edit/stage/:stagePreviewId/preview',
                element: <StagePreview />
              },
              { path: '*', element: <Navigate to={''} replace /> }
            ]
          },
          {
            path: 'assessments',
            element: <ContestContainer key="assessment" />,
            children: [
              { index: true, element: <ContestList assessment={true} /> },
              { path: 'create', element: <ContestCreate assessment={true} /> },
              {
                path: ':contestId/ppt-stage/:stageId',
                element: <InterviewStage ppt={true} assessment={true} />
              },
              {
                path: ':contestId/interview-stage/:stageId/candidate/:candidateId',
                element: <InterviewStage assessment={true} />
              },
              {
                path: ':contestId/edit',
                element: <EditContest assessment={true} />
              },
              {
                path: ':contestId/candidate-summary/:candidateId',
                element: <CandidateSummary />
              },
              {
                path: ':contestId/edit/stage/:stageId',
                element: <StageDetails assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/mcq-evaluate/:attemptId',
                element: <EvaluateMCQAttempt assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/psychometric-evaluate/:attemptId',
                element: <EvaluateMCQAttempt assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/video-response-evaluate/:attemptId',
                element: <VideoResponseEvaluation assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/ai-screening-evaluate/:attemptId',
                element: <EvaluateAIScreening assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/evaluate/:attemptId',
                element: <EvaluateAttempt assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/suspicious-activity/:attemptId',
                element: <SuspiciousActivity assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stagePreviewId/preview',
                element: <StagePreview assessment={true} />
              },
              { path: '*', element: <Navigate to={''} replace /> }
            ]
          },
          {
            path: 'challenges',
            element: <ChallengeContainer />,
            children: [
              { index: true, element: <ChallengeList /> },
              { path: 'create', element: <CreateChallengeWrapper /> },
              { path: 'editor', element: <ChallengeEditor /> },
              { path: 'editor/sql', element: <SQLChallengeEditor /> },
              { path: ':challengeId/edit', element: <EditChallenge /> },
              { path: '*', element: <Navigate to={''} replace /> }
            ]
          },
          {
            path: 'settings',
            element: <SettingsContainer />,
            children: [
              { index: true, element: <Navigate to="users" replace /> },
              { path: 'users', element: <UsersSettings /> },
              { path: 'workspaces', element: <WorkSpaceSettings /> },
              { path: 'integrations', element: <Integrations /> },
              { path: 'config', element: <ThemeConfigSettings /> },
              { path: 'careers-config', element: <CareersConfig /> },
              { path: 'profile', element: <ProfileSettings /> },
              { path: 'user-approvals', element: <UserApprovals /> },
              { path: '*', element: <Navigate to={'users'} replace /> }
            ]
          },
          { path: '*', element: <Navigate to={'/'} replace /> }
        ]
      },
      // Global candidate restricted routes
      {
        path: '/candidates',
        element: <CandidateContainer />,
        children: [
          { index: true, element: <Navigate to="login" replace /> },
          {
            path: 'login',
            element: <CandidateRestrictedRoute />,
            children: [
              { index: true, element: <Login globalCandidatePath={true} /> }
            ]
          },
          {
            path: 'signup',
            element: <CandidateRestrictedRoute />,
            children: [
              { index: true, element: <Signup globalCandidatePath={true} /> }
            ]
          },
          {
            path: 'verify',
            element: <CandidateRestrictedRoute />,
            children: [
              {
                index: true,
                element: <VerifyUser globalCandidatePath={true} />
              }
            ]
          },
          {
            path: 'forgot-password',
            element: <CandidateRestrictedRoute />,
            children: [
              {
                index: true,
                element: <ForgotPassword globalCandidatePath={true} />
              }
            ]
          },
          {
            path: 'forgot-password/verify',
            element: <CandidateRestrictedRoute />,
            children: [
              {
                index: true,
                element: <ForgotPasswordVerify globalCandidatePath={true} />
              }
            ]
          },
          { path: '*', element: <Navigate to="login" replace /> }
        ]
      },
      {
        path: '/candidate/:contestUri/:token',
        element: <PasswordlessRoute />
      },

      // Candidate routes
      {
        path: '/c',
        element: <CandidateContainer />,
        children: [
          {
            path: '',
            element: <CandidatePrivateRoute />,
            children: [{ index: true, element: <CandidateHome /> }]
          },
          {
            path: 'profile',
            element: <CandidatePrivateRoute />,
            children: [{ index: true, element: <ProfileSettings /> }]
          },
          {
            path: 'subscription',
            element: <CandidatePrivateRoute />,
            children: [
              {
                index: true,
                element: <Subscription isCandidate={true} />
              }
            ]
          },
          // Candidates Contest Routes
          {
            path: ':contestUri',
            element: <CandidateContainer />,
            children: [
              { index: true, element: <Navigate to="stages" replace /> },
              {
                path: 'stages',
                element: <CandidatePrivateRoute />,
                children: [{ index: true, element: <ContestHome /> }]
              },
              {
                path: 'login',
                element: <CandidateRestrictedRoute />,
                children: [
                  { index: true, element: <Login candidatePath={true} /> }
                ]
              },
              {
                path: 'signup',
                element: <CandidateRestrictedRoute />,
                children: [
                  { index: true, element: <Signup candidatePath={true} /> }
                ]
              },
              {
                path: 'verify',
                element: <CandidateRestrictedRoute />,
                children: [
                  { index: true, element: <VerifyUser candidatePath={true} /> }
                ]
              },
              {
                path: 'forgot-password',
                element: <CandidateRestrictedRoute />,
                children: [
                  {
                    index: true,
                    element: <ForgotPassword candidatePath={true} />
                  }
                ]
              },
              { path: 'registration', element: <CandidateRegistration /> },
              {
                path: 'registration/success',
                element: <CandidateRegistrationDone />
              },
              {
                path: 'forgot-password/verify',
                element: <CandidateRestrictedRoute />,
                children: [
                  {
                    index: true,
                    element: <ForgotPasswordVerify candidatePath={true} />
                  }
                ]
              },
              {
                path: 'verify-invited',
                element: <VerifyInvitedUser candidatePath={true} />
              },
              {
                path: 'demo-challenge/:demoChallengeId',
                element: <CandidatePrivateRoute />,
                children: [{ index: true, element: <DemoChallenge /> }]
              },
              {
                path: ':stageId',
                element: <CandidatePrivateRoute />,
                children: [{ index: true, element: <ContestStage /> }]
              },
              {
                path: ':contestId/ppt-stage/:stageId',
                element: <CandidatePrivateRoute />,
                children: [
                  { index: true, element: <InterviewStage ppt={true} /> }
                ]
              },
              {
                path: ':contestId/interview-stage/:stageId',
                element: <CandidatePrivateRoute />,
                children: [{ index: true, element: <InterviewStage /> }]
              },
              {
                path: ':contestId/ai-screening-stage/:stageId',
                element: <CandidatePrivateRoute />,
                children: [{ index: true, element: <AIScreeningStage /> }]
              },
              {
                path: ':contestId/video-response-stage/:stageId',
                element: <CandidatePrivateRoute />,
                children: [{ index: true, element: <VideoResponseStageType /> }]
              },
              {
                path: ':contestId/practice-result/:attemptId',
                element: <CandidatePrivateRoute />,
                children: [{ index: true, element: <CandidateResult /> }]
              }
            ]
          }
        ]
      },
      // Dev user routes
      {
        path: '/dev/login',
        element: <RestrictedRoute />,
        children: [{ index: true, element: <Login devUser={true} /> }]
      },
      {
        path: '/dev/forgot-password',
        element: <RestrictedRoute />,
        children: [
          { index: true, element: <ForgotPassword developerPath={true} /> }
        ]
      },
      {
        path: '/dev/forgot-password/verify',
        element: <RestrictedRoute />,
        children: [
          {
            index: true,
            element: <ForgotPasswordVerify developerPath={true} />
          }
        ]
      },
      // Public routes
      {
        path: '/share',
        element: <PublicShareContainer />,
        children: [
          { path: ':publicId', element: <CandidateReport /> },
          {
            path: ':publicId/assessments',
            element: <ContestContainer key="public" />,
            children: [
              {
                path: ':contestId/edit/stage/:stageId/mcq-evaluate/:attemptId',
                element: <EvaluateMCQAttempt assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/psychometric-evaluate/:attemptId',
                element: <EvaluateMCQAttempt assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/video-response-evaluate/:attemptId',
                element: <VideoResponseEvaluation assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/ai-screening-evaluate/:attemptId',
                element: <EvaluateAIScreening assessment={true} />
              },
              {
                path: ':contestId/edit/stage/:stageId/evaluate/:attemptId',
                element: <EvaluateAttempt assessment={true} />
              },
              {
                path: ':contestId/candidate-summary/:candidateId',
                element: <CandidateSummary />
              },
              { path: '*', element: <Navigate to={'..'} replace /> }
            ]
          },
          {
            path: ':publicId/contests',
            element: <ContestContainer key="public" />,
            children: [
              {
                path: ':contestId/edit/stage/:stageId/mcq-evaluate/:attemptId',
                element: <EvaluateMCQAttempt assessment={false} />
              },
              {
                path: ':contestId/edit/stage/:stageId/psychometric-evaluate/:attemptId',
                element: <EvaluateMCQAttempt assessment={false} />
              },
              {
                path: ':contestId/edit/stage/:stageId/video-response-evaluate/:attemptId',
                element: <VideoResponseEvaluation assessment={false} />
              },
              {
                path: ':contestId/edit/stage/:stageId/ai-screening-evaluate/:attemptId',
                element: <EvaluateAIScreening assessment={false} />
              },
              {
                path: ':contestId/edit/stage/:stageId/evaluate/:attemptId',
                element: <EvaluateAttempt assessment={false} />
              },
              {
                path: ':contestId/candidate-summary/:candidateId',
                element: <CandidateSummary />
              },
              { path: '*', element: <Navigate to={'..'} replace /> }
            ]
          }
        ]
      },
      // Auxiliary Device Route (for mobile proctoring)
      {
        path: '/:candidateId/auxiliary-device/assessment/:contestId/stage/:stageId/:attemptId',
        element: <AuxiliaryDevice />
      }
    ]
  }
])

export default router
